import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, Subscription, from, retry, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import AppConstants from '../constants/app.constants';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { StorageService } from './storage.service';
import { ProfileService } from './profile.service';
import { TimezoneService } from './timezone.service';
import { Router } from '@angular/router';
@Injectable({
  providedIn: 'root',
})
export class HttpService {
  apiURL: string = environment.apiUrl;

  // Endpoints that must not trigger a session refresh (unauthenticated or session related)
  private static readonly SESSION_REFRESH_EXEMPT_ENDPOINTS = [
    'auth/',
    'user/other/getCheckUser',
  ];

  public token: string = '';
  public token_: string = '';
  private isHandlingAuthError = false;
  private isRefreshingSession = false;
  deviceInfo: any;

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private profileService: ProfileService,
    private timezoneService: TimezoneService,
    private router: Router,
  ) {
  }

  private async getHttpOptions(customHeaders: Record<string, string> = {}): Promise<{ headers: HttpHeaders }> {
    const token = await this.storageService.getItemIonic(AppConstants.AUTH_TOKEN) || '';
    const token_ = await this.storageService.getItemIonic(AppConstants.RW_HASH_TOKEN) || '';
    const token__ = await this.storageService.getItemIonic(AppConstants.x_refresh_token) || '';
    const timezoneOffset = this.timezoneService.timezoneName;

    return {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        RW_AUTH: `Bearer ${token_}`,
        x_refresh_token: `${token__}`,
        timezone: timezoneOffset,
        ...customHeaders,
      })
    };
  }

  // Rotates the auth tokens after an API call; failures must never affect the caller
  private async refreshSession(endPoint: string): Promise<void> {
    if (this.isRefreshingSession || this.isHandlingAuthError) {
      return;
    }
    const path = `${endPoint ?? ''}`;
    if (HttpService.SESSION_REFRESH_EXEMPT_ENDPOINTS.some((exempt) => path.includes(exempt))) {
      return;
    }

    this.isRefreshingSession = true;
    try {
      await this.profileService.ensureAuthenticated();
    } catch (error) {
      console.error('Session refresh failed after API call:', error);
    } finally {
      this.isRefreshingSession = false;
    }
  }

  async get(endPoint: any, customHeaders: Record<string, string> = {}): Promise<any> {
    let header = await this.getHttpOptions(customHeaders);
    return new Promise((resolve, reject) => {
      this.http.get(`${this.apiURL}${endPoint}`, header).subscribe({
        next: (response) => {
          resolve(response);
          void this.refreshSession(endPoint);
        },
        error: (error) => {
          console.error('Error occurred:', error);
          this.handleError(error);
          reject(throwError(() => error));
        }
      });
    });
  }

  async post(endPoint: string,
    data: any,
    customHeaders: Record<string, string> = {}): Promise<any> {
    let header = await this.getHttpOptions(customHeaders);
    return new Promise((resolve, reject) => {
      this.http.post(`${this.apiURL}${endPoint}`, data, header).subscribe({
        next: (response) => {
          resolve(response);
          void this.refreshSession(endPoint);
        },
        error: (error) => {
          console.error('Error occurred:', error);
          this.handleError(error);
          reject(throwError(() => error));
        }
      });
    });
  }

  public postAuth(endPoint: any, data: any): Observable<any> {
    return from(this.getHttpOptions()).pipe(
      switchMap((options) =>
        this.http.post<any>(`${this.apiURL}${endPoint}`, data, options)
      ),
      retry(1),
      catchError((error: HttpErrorResponse) => {
        this.handleError(error);
        return throwError(() => error);
      })
    );
  }

  put(endPoint: any,
    data: any,
    customHeaders: Record<string, string> = {}
  ): Promise<any>  {
    return new Promise(async (resolve, reject) => {
      let header = await this.getHttpOptions(customHeaders);
      this.http.put(`${this.apiURL}${endPoint}`, data, header).subscribe({
        next: (response) => {
          resolve(response);
          void this.refreshSession(endPoint);
        },
        error: (error) => {
          console.error('Error occurred:', error);
          this.handleError(error);
          reject(throwError(() => error));
        }
      });
    });
  }

  patch(
    endPoint: string,
    data: any,
    customHeaders: Record<string, string> = {}
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let header = {};
      // if (endPoint !== 'auth/user/logout') {
        
      // }
      header = await this.getHttpOptions(customHeaders);
      this.http.patch(`${this.apiURL}${endPoint}`, data, header).subscribe({
        next: (response) => {
          resolve(response);
          void this.refreshSession(endPoint);
        },
        error: (error) => {
          console.error('Error occurred:', error);
          this.handleError(error);
          reject(throwError(() => error));
        }
      });
    });
  }

  delete(endPoint: string, data: any, customHeaders: Record<string, string> = {}): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let header = await this.getHttpOptions(customHeaders);
      this.http.delete(`${this.apiURL}${endPoint}`, {
        ...header,
        body: data,
      }).subscribe({
        next: (response) => {
          resolve(response);
          void this.refreshSession(endPoint);
        },
        error: (error) => {
          console.error('Error occurred:', error);
          this.handleError(error);
          reject(throwError(() => error));
        }
      });
    });
  }

  private async handleError(error: HttpErrorResponse) {
    console.error('HTTP Error:', error);
    // Check for auth token expired error
    if (error.error?.message === 'auth_token_expired_or_invalid' && !this.isHandlingAuthError) {
      this.isHandlingAuthError = true;
      console.warn('Auth token expired or invalid. Attempting logout...');
      // Try to logout
      this.deviceInfo = await this.storageService.getItemIonic(
        AppConstants.DEVICE_INFO
      );
      const payload = {
        playerId: this.deviceInfo.uuid,
        deviceType: 2,
      };
      // Call logOut which handles all cleanup and navigation
      this.profileService.logOut();
      this.isHandlingAuthError = false;
    }
  }

  private clearDataAndNavigateToLogin() {
    console.warn('Clearing all local data and navigating to login page...');
    // Clear both localStorage and Ionic storage
    this.storageService.clear();
    this.storageService.clearIonic().catch(err => {
      console.error('Error clearing Ionic storage:', err);
    });
    // Navigate to login page
    this.router.navigateByUrl('/auth/login', { replaceUrl: true });
  }
}
